import {useCallback} from 'react';

import {NavigateFunction} from 'react-router';

import {CGetTokenConfig, GetTokenInput} from '@/services';

type UseGetTokenParams = {
  phone?: string;
  sharedKey?: string;
  smsCode?: string;
  setSmsCode: (value: string) => void;
  navigate: NavigateFunction;
  amplifyGetToken: (input: GetTokenInput) => Promise<string>;
};

/**
 * Resolves the SMS confirmation code by polling the shared-key endpoint,
 * retrying up to CGetTokenConfig.MAX_RETRIES times and skipping any result
 * that matches the code the user already typed.
 */
export function useGetToken({
  phone,
  sharedKey,
  smsCode,
  setSmsCode,
  navigate,
  amplifyGetToken,
}: UseGetTokenParams) {
  const tryGetToken = useCallback(
    async (phone: string, sharedKey: string): Promise<string> => {
      try {
        const result = await amplifyGetToken({
          target: phone,
          shared_key: sharedKey,
        });

        return result || '';
      } catch {
        return '';
      }
    },
    [amplifyGetToken],
  );

  const retryGetToken = useCallback(
    async (
      phone: string,
      sharedKey: string,
      smsCode?: string,
    ): Promise<string | null> => {
      for (let attempt = 1; attempt <= CGetTokenConfig.MAX_RETRIES; attempt++) {
        const result = await tryGetToken(phone, sharedKey);

        if (smsCode && result === smsCode) {
          continue;
        }

        return result;
      }

      return null;
    },
    [tryGetToken],
  );

  const getToken = useCallback(
    async (needSetSMSCode: boolean = true) => {
      if (!phone || !sharedKey) {
        return navigate('/authentication/sign-up');
      }

      const result = await retryGetToken(phone, sharedKey, smsCode);

      if (needSetSMSCode) setSmsCode(result);
      return result;
    },
    [navigate, phone, retryGetToken, setSmsCode, sharedKey, smsCode],
  );

  return {getToken};
}
